"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _trimStart2 = _interopRequireDefault(require("lodash/trimStart"));

var _flow2 = _interopRequireDefault(require("lodash/flow"));

var _semaphore = _interopRequireDefault(require("semaphore"));

var _commonTags = require("common-tags");

var _netlifyCmsLibUtil = require("netlify-cms-lib-util");

var _netlifyCmsLibAuth = require("netlify-cms-lib-auth");

var _AuthenticationPage = _interopRequireDefault(require("./AuthenticationPage"));

var _API = _interopRequireDefault(require("./API"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _templateObject() {
  const data = _taggedTemplateLiteral(["\n        Repo \"", "\" not found.\n\n        Please ensure the repo information is spelled correctly.\n\n        If the repo is private, make sure you're logged into a Bitbucket account with access.\n      "]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const MAX_CONCURRENT_DOWNLOADS = 10; // Implementation wrapper class

class BitbucketBackend {
  constructor(config) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _defineProperty(this, "apiRequestFunction", async req => {
      const token = this.refreshedTokenPromise ? await this.refreshedTokenPromise : this.token;
      return (0, _flow2.default)([_netlifyCmsLibUtil.unsentRequest.withHeaders({
        Authorization: "Bearer ".concat(token)
      }), _netlifyCmsLibUtil.unsentRequest.performRequest, (0, _netlifyCmsLibUtil.then)(async res => {
        if (res.status === 401) {
          const json = await res.json().catch(() => null);

          if (json && json.type === 'error' && /^access token expired/i.test(json.error.message)) {
            const newToken = await this.getRefreshedAccessToken();

            const reqWithNewToken = _netlifyCmsLibUtil.unsentRequest.withHeaders({
              Authorization: "Bearer ".concat(newToken)
            }, req);

            return _netlifyCmsLibUtil.unsentRequest.performRequest(reqWithNewToken);
          }
        }

        return res;
      })])(req);
    });

    _defineProperty(this, "fetchFiles", files => {
      const sem = (0, _semaphore.default)(MAX_CONCURRENT_DOWNLOADS);
      const promises = [];
      files.forEach(file => {
        promises.push(new Promise(resolve => sem.take(() => this.api.readFile(file.path, file.id).then(data => {
          resolve({
            file,
            data
          });
          sem.leave();
        }).catch(function () {
          let error = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
          sem.leave();
          console.error("failed to load file from BitBucket: ".concat(file.path));
          resolve({
            error
          });
        }))));
      });
      return Promise.all(promises).then(loadedEntries => loadedEntries.filter(loadedEntry => !loadedEntry.error));
    });

    this.config = config;
    this.options = _objectSpread({
      proxied: false,
      API: null,
      updateUserCredentials: async () => null
    }, options);

    if (this.options.useWorkflow) {
      throw new Error('The BitBucket backend does not support the Editorial Workflow.');
    }

    if (!this.options.proxied && !config.getIn(['backend', 'repo'], false)) {
      throw new Error('The BitBucket backend needs a "repo" in the backend configuration.');
    }

    this.api = this.options.API || null;
    this.updateUserCredentials = this.options.updateUserCredentials;
    this.repo = config.getIn(['backend', 'repo'], '');
    this.branch = config.getIn(['backend', 'branch'], 'master');
    this.api_root = config.getIn(['backend', 'api_root'], 'https://api.bitbucket.org/2.0');
    this.base_url = config.get('base_url');
    this.site_id = config.get('site_id');
    this.token = '';
  }

  authComponent() {
    return _AuthenticationPage.default;
  }

  setUser(user) {
    this.token = user.token;
    this.api = new _API.default({
      requestFunction: this.apiRequestFunction,
      branch: this.branch,
      repo: this.repo
    });
  }

  restoreUser(user) {
    return this.authenticate(user);
  }

  async authenticate(state) {
    this.token = state.token;
    this.refreshToken = state.refresh_token;
    this.api = new _API.default({
      requestFunction: this.apiRequestFunction,
      branch: this.branch,
      repo: this.repo,
      api_root: this.api_root
    });
    const user = await this.api.user();
    const isCollab = await this.api.hasWriteAccess(user).catch(error => {
      error.message = (0, _commonTags.stripIndent)(_templateObject(), this.repo);
      throw error;
    }); // Unauthorized user

    if (!isCollab) {
      throw new Error('Your BitBucket user account does not have access to this repo.');
    } // Autorized user


    return _objectSpread({}, user, {
      token: state.token,
      refresh_token: state.refresh_token
    });
  }

  getRefreshedAccessToken() {
    if (this.refreshedTokenPromise) {
      return this.refreshedTokenPromise;
    } // instantiating a new Authenticator on each refresh isn't ideal,


    if (!this.auth) {
      const cfg = {
        base_url: this.base_url,
        site_id: this.site_id
      };
      this.authenticator = new _netlifyCmsLibAuth.NetlifyAuthenticator(cfg);
    }

    this.refreshedTokenPromise = this.authenticator.refresh({
      provider: 'bitbucket',
      refresh_token: this.refreshToken
    }).then((_ref) => {
      let {
        token,
        refresh_token
      } = _ref;
      this.token = token;
      this.refreshToken = refresh_token;
      this.refreshedTokenPromise = undefined;
      this.updateUserCredentials({
        token,
        refresh_token
      });
      return token;
    });
    return this.refreshedTokenPromise;
  }

  logout() {
    this.token = null;
    return;
  }

  getToken() {
    if (this.refreshedTokenPromise) {
      return this.refreshedTokenPromise;
    }

    return Promise.resolve(this.token);
  }

  entriesByFolder(collection, extension) {
    const listPromise = this.api.listFiles(collection.get('folder'));
    return (0, _netlifyCmsLibUtil.resolvePromiseProperties)({
      files: listPromise.then((_ref2) => {
        let {
          entries
        } = _ref2;
        return entries;
      }).then((0, _netlifyCmsLibUtil.filterByPropExtension)(extension, 'path')).then(this.fetchFiles),
      cursor: listPromise.then((_ref3) => {
        let {
          cursor
        } = _ref3;
        return cursor;
      })
    }).then((_ref4) => {
      let {
        files,
        cursor
      } = _ref4;
      files[_netlifyCmsLibUtil.CURSOR_COMPATIBILITY_SYMBOL] = cursor;
      return files;
    });
  }

  allEntriesByFolder(collection, extension) {
    return this.api.listAllFiles(collection.get('folder')).then((0, _netlifyCmsLibUtil.filterByPropExtension)(extension, 'path')).then(this.fetchFiles);
  }

  entriesByFiles(collection) {
    const files = collection.get('files').map(collectionFile => ({
      path: collectionFile.get('file'),
      label: collectionFile.get('label')
    }));
    return this.fetchFiles(files);
  }

  getEntry(collection, slug, path) {
    return this.api.readFile(path).then(data => ({
      file: {
        path
      },
      data
    }));
  }

  getMedia() {
    return this.api.listAllFiles(this.config.get('media_folder')).then(files => files.map((_ref5) => {
      let {
        id,
        name,
        path
      } = _ref5;
      return {
        id,
        name,
        path,
        displayURL: {
          id,
          path
        }
      };
    }));
  }

  getMediaDisplayURL(displayURL) {
    this._mediaDisplayURLSem = this._mediaDisplayURLSem || (0, _semaphore.default)(MAX_CONCURRENT_DOWNLOADS);
    const {
      id,
      path
    } = displayURL;
    return new Promise((resolve, reject) => this._mediaDisplayURLSem.take(() => this.api.readFile(path, id, {
      parseText: false
    }).then(blob => URL.createObjectURL(blob)).then(resolve, reject).finally(() => this._mediaDisplayURLSem.leave())));
  }

  persistEntry(entry, mediaFiles) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return this.api.persistFiles([entry], options);
  }

  async persistMedia(mediaFile) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    await this.api.persistFiles([mediaFile], options);
    const {
      value,
      path,
      fileObj
    } = mediaFile;
    return {
      name: value,
      size: fileObj.size,
      path: (0, _trimStart2.default)(path, '/k')
    };
  }

  deleteFile(path, commitMessage, options) {
    return this.api.deleteFile(path, commitMessage, options);
  }

  traverseCursor(cursor, action) {
    return this.api.traverseCursor(cursor, action).then(async (_ref6) => {
      let {
        entries,
        cursor: newCursor
      } = _ref6;
      return {
        entries: await Promise.all(entries.map(file => this.api.readFile(file.path, file.id).then(data => ({
          file,
          data
        })))),
        cursor: newCursor
      };
    });
  }

}

exports.default = BitbucketBackend;